import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import siteData from "../../siteData";
import { useMagnetic } from "../../hooks/useMagnetic";

/* ──── ANIMATION VARIANTS ──── */

const navAnim = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1], delay: 0.2 },
  },
};

const heroContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.8 },
  },
};

const badgeAnim = {
  hidden: { opacity: 0, y: 20, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1] },
  },
};

const charAnim = {
  hidden: { y: "120%", opacity: 0 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.19, 1, 0.22, 1],
      delay: i * 0.04,
    },
  }),
};

const taglineAnim = {
  hidden: { opacity: 0, y: 20, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1] },
  },
};

const ctaAnim = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.19, 1, 0.22, 1] },
  },
};

const socialContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const socialItem = {
  hidden: { opacity: 0, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 200, damping: 15 },
  },
};

const scrollAnim = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { delay: 3, duration: 1, ease: "easeOut" },
  },
};

/* Split text into individual characters */
function SplitText({ text, className, startIndex = 0 }) {
  return (
    <motion.span className={className} variants={{ hidden: {}, visible: {} }}>
      {text.split("").map((char, i) =>
        char === " " ? (
          <span key={i} className="hero-char hero-char-space" />
        ) : (
          <motion.span
            key={i}
            className="hero-char"
            variants={charAnim}
            custom={startIndex + i}
          >
            {char}
          </motion.span>
        ),
      )}
    </motion.span>
  );
}

/* Firefly with Framer Motion for smoother animation */
function Firefly({ x, y, size, delay, duration }) {
  const randomPath = useMemo(
    () => ({
      x: [0, (Math.random() - 0.5) * 120, (Math.random() - 0.5) * 80, 0],
      y: [0, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 60, 0],
      opacity: [0, 0.8, 0.3, 0.9, 0],
    }),
    [],
  );

  return (
    <motion.div
      className="firefly"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
      }}
      animate={randomPath}
      transition={{
        duration: duration,
        delay: delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <div className="firefly-inner" />
    </motion.div>
  );
}

export default function Header({ activeSection = "home" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), []);

  // Magnetic effect for CTA buttons
  const primaryCta = useMagnetic(0.25);
  const secondaryCta = useMagnetic(0.25);

  /* Firefly data — fewer, larger, more organic */
  const fireflies = useMemo(
    () =>
      Array.from({ length: 100 }, (_, i) => ({
        id: i,
        x: 10 + Math.random() * 80,
        y: 10 + Math.random() * 80,
        size: 3 + Math.random() * 5,
        duration: 8 + Math.random() * 10,
        delay: Math.random() * 5,
      })),
    [],
  );

  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "resume", label: "Resume" },
    { id: "portfolio", label: "Works" },
    { id: "contact", label: "Contact" },
  ];

  const firstName = siteData.firstName || siteData.name.split(" ")[0];
  const lastName =
    siteData.lastName || siteData.name.split(" ").slice(1).join(" ");

  return (
    <>
      {/* ──── NAVIGATION ──── */}
      <motion.div
        className={`nav${scrolled ? " scrolled" : ""}`}
        initial="hidden"
        animate="visible"
        variants={navAnim}
      >
        <div className="nav-inner">
          <a href="#home" className="nav-logo smoothscroll">
            {siteData.name}
          </a>
          <div className={`nav-links${menuOpen ? " open" : ""}`}>
            {navItems.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className={`smoothscroll${activeSection === id ? " active" : ""}`}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </a>
            ))}
          </div>
          <button
            className="mobile-menu-btn"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </motion.div>

      {/* ──── HERO SECTION ──── */}
      <section className="hero" id="home">
        {/* Rainforest background image with cinematic zoom */}
        <motion.div
          className="hero-bg"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 3, ease: [0.19, 1, 0.22, 1] }}
        />

        {/* Atmospheric mist layers */}
        <div className="hero-mist hero-mist-1" />
        <div className="hero-mist hero-mist-2" />

        {/* Dappled canopy light */}
        <div className="hero-gradient-mesh">
          <div className="hero-gradient-orb hero-gradient-orb-1" />
          <div className="hero-gradient-orb hero-gradient-orb-2" />
          <div className="hero-gradient-orb hero-gradient-orb-3" />
        </div>

        {/* Vignette */}
        <div className="hero-vignette" />

        {/* Fireflies */}
        {fireflies.map((f) => (
          <Firefly key={f.id} {...f} />
        ))}

        {/* Hero content */}
        <motion.div
          className="hero-content"
          initial="hidden"
          animate="visible"
          variants={heroContainer}
        >
          {/* Badge */}
          <motion.div className="hero-badge" variants={badgeAnim}>
            <span className="badge-dot" />
            Available for opportunities
          </motion.div>

          {/* Massive name with character-by-character reveal */}
          <motion.div
            className="hero-name"
            variants={{ hidden: {}, visible: {} }}
          >
            <motion.div
              className="hero-name-wrapper"
              variants={{ hidden: {}, visible: {} }}
            >
              <motion.div
                className="hero-name-line"
                variants={{ hidden: {}, visible: {} }}
              >
                <SplitText text="Hi, I'm" startIndex={0} />
              </motion.div>
            </motion.div>
            <motion.div
              className="hero-name-wrapper"
              variants={{ hidden: {}, visible: {} }}
            >
              <motion.div
                className="hero-name-line"
                variants={{ hidden: {}, visible: {} }}
              >
                <SplitText
                  text={firstName}
                  className="hero-name-accent"
                  startIndex={7}
                />
                <span className="hero-char hero-char-space" />
                <SplitText
                  text={lastName}
                  className="hero-name-accent"
                  startIndex={7 + firstName.length}
                />
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Tagline */}
          <motion.p className="hero-tagline" variants={taglineAnim}>
            A <strong>{siteData.role}</strong> building impactful web
            applications with modern technologies. Currently at{" "}
            <strong>{siteData.currentCompany}</strong>.
          </motion.p>

          {/* CTAs with magnetic effect */}
          <motion.div className="hero-ctas" variants={ctaAnim}>
            <motion.a
              ref={primaryCta.ref}
              href="#contact"
              className="btn btn-primary smoothscroll"
              style={{ x: primaryCta.x, y: primaryCta.y }}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 0 60px rgba(0,230,138,0.3)",
              }}
              whileTap={{ scale: 0.97 }}
            >
              Get in Touch
            </motion.a>
            <motion.a
              ref={secondaryCta.ref}
              href={siteData.resumeFile}
              download
              className="btn btn-secondary"
              style={{ x: secondaryCta.x, y: secondaryCta.y }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              Download Resume
            </motion.a>
          </motion.div>

          {/* Social links */}
          <motion.div className="hero-social" variants={socialContainer}>
            <motion.a
              href={siteData.linkedin}
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
              variants={socialItem}
              whileHover={{ y: -3, boxShadow: "0 0 30px rgba(0,230,138,0.2)" }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="fa fa-linkedin" />
            </motion.a>
            <motion.a
              href={siteData.github}
              aria-label="GitHub"
              target="_blank"
              rel="noopener noreferrer"
              variants={socialItem}
              whileHover={{ y: -3, boxShadow: "0 0 30px rgba(0,230,138,0.2)" }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="fa fa-github" />
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="scroll-indicator"
          initial="hidden"
          animate="visible"
          variants={scrollAnim}
        >
          <a href="#about" className="smoothscroll">
            <span>Scroll</span>
            <div className="scroll-line" />
          </a>
        </motion.div>
      </section>
    </>
  );
}
